import { createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import { createAppThunk } from '../createAppThunk';
import type { DocumentDto, RequestUploadDto, RequestUploadResponseDto } from '@/types/Api';
import {
  requestDocumentUpload as requestDocumentUploadApi,
  completeDocumentUpload as completeDocumentUploadApi,
  getDocuments as getDocumentsApi,
  deleteDocument as deleteDocumentApi,
  uploadFileToS3 as uploadFileToS3Api,
} from '@/lib/apiClient';
import type { NormalizedError } from '@/types/NormalizedError';
import { toAppError } from '@/lib/toAppError';

interface DocumentState {
  documents: DocumentDto[];
  uploadStatus: 'idle' | 'requesting-url' | 'uploading' | 'completing' | 'succeeded' | 'failed';
  fetchStatus: 'idle' | 'loading' | 'succeeded' | 'failed';
  deleteStatus: 'idle' | 'loading' | 'succeeded' | 'failed';
  error: NormalizedError | null;
  fetchError: NormalizedError | null;
  deleteError: NormalizedError | null;
  uploadProgress: number;
  currentUpload: RequestUploadResponseDto | null;
}

const initialState: DocumentState = {
  documents: [],
  uploadStatus: 'idle',
  fetchStatus: 'idle',
  deleteStatus: 'idle',
  error: null,
  fetchError: null,
  deleteError: null,
  uploadProgress: 0,
  currentUpload: null,
};

// Upload document (full flow)
export const uploadDocument = createAppThunk<
  DocumentDto,
  {
    orgId: string;
    uploadData: RequestUploadDto;
    file: File;
    token: string;
  }
>('document/uploadDocument', async ({ orgId, uploadData, file, token }, thunkAPI) => {
  console.log('uploadDocument: Starting upload with:', {
    orgId,
    uploadData,
    fileName: file.name,
    tokenLength: token?.length,
  });

  try {
    // Step 1: Request upload URL
    console.log('uploadDocument: Step 1 - Requesting upload URL');
    thunkAPI.dispatch(setUploadStatus('requesting-url'));
    const { data: uploadResponse, error: uploadError } = await requestDocumentUploadApi(
      orgId,
      uploadData,
      token,
    );

    if (uploadError || !uploadResponse) {
      console.error('uploadDocument: Step 1 failed:', uploadError);
      return thunkAPI.rejectWithValue(
        uploadError ?? toAppError(new Error('Failed to get upload URL')),
      );
    }

    console.log('uploadDocument: Step 1 successful, received upload response:', uploadResponse);
    thunkAPI.dispatch(setCurrentUpload(uploadResponse));

    // Step 2: Upload file to S3
    console.log('uploadDocument: Step 2 - Uploading file to S3:', uploadResponse.uploadUrl);
    thunkAPI.dispatch(setUploadStatus('uploading'));
    await uploadFileToS3Api(uploadResponse.uploadUrl, file, (progress: number) => {
      thunkAPI.dispatch(setUploadProgress(progress));
    });
    console.log('uploadDocument: Step 2 successful - File uploaded to S3');

    // Step 3: Complete upload
    console.log(
      'uploadDocument: Step 3 - Completing upload with documentId:',
      uploadResponse.documentId,
    );
    thunkAPI.dispatch(setUploadStatus('completing'));
    const { error: completeError } = await completeDocumentUploadApi(
      orgId,
      uploadResponse.documentId,
      token,
    );

    if (completeError) {
      console.error('uploadDocument: Step 3 failed:', completeError);
      return thunkAPI.rejectWithValue(completeError);
    }

    console.log('uploadDocument: Step 3 successful - Upload completed');

    // Return a minimal document object - we'll fetch full details separately if needed
    const completedDocument: DocumentDto = {
      id: uploadResponse.documentId,
      entityType: uploadData.entity_type,
      entityId: uploadData.entity_id,
      originalFilename: uploadData.filename,
      mimeType: uploadData.mimeType.toString(),
      fileSize: uploadData.size.toString(),
      status: 'ready',
      uploaderId: '', // Will be filled by backend
      storageKey: '', // Will be filled by backend
      organizationId: orgId, // Add the missing organizationId
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };

    return completedDocument;
  } catch (error) {
    return thunkAPI.rejectWithValue(toAppError(error));
  }
});

// Fetch documents
export const fetchDocuments = createAppThunk<
  DocumentDto[],
  {
    orgId: string;
    filters?: { entityType?: string; entityId?: string; uploaderId?: string };
    token: string;
  }
>('document/fetchDocuments', async ({ orgId, filters = {}, token }, thunkAPI) => {
  const { data, error } = await getDocumentsApi(orgId, filters, token);
  if (error) return thunkAPI.rejectWithValue(error);
  if (!data) return [];
  return data;
});

// Delete document
export const deleteDocument = createAppThunk<
  string,
  { orgId: string; documentId: string; token: string }
>('document/deleteDocument', async ({ orgId, documentId, token }, thunkAPI) => {
  const { error } = await deleteDocumentApi(orgId, documentId, token);
  if (error) return thunkAPI.rejectWithValue(error);
  return documentId;
});

export const documentSlice = createSlice({
  name: 'document',
  initialState,
  reducers: {
    setUploadStatus: (state, action: PayloadAction<DocumentState['uploadStatus']>) => {
      state.uploadStatus = action.payload;
    },
    setUploadProgress: (state, action: PayloadAction<number>) => {
      state.uploadProgress = action.payload;
    },
    setCurrentUpload: (state, action: PayloadAction<RequestUploadResponseDto | null>) => {
      state.currentUpload = action.payload;
    },
    clearUploadState: (state) => {
      state.uploadStatus = 'idle';
      state.uploadProgress = 0;
      state.currentUpload = null;
      state.error = null;
    },
    clearErrors: (state) => {
      state.error = null;
      state.fetchError = null;
      state.deleteError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Upload document reducers
      .addCase(uploadDocument.pending, (state) => {
        state.uploadStatus = 'requesting-url';
        state.error = null;
        state.uploadProgress = 0;
      })
      .addCase(uploadDocument.fulfilled, (state, action) => {
        state.uploadStatus = 'succeeded';
        state.uploadProgress = 100;
        // Add the uploaded document to the list
        state.documents.unshift(action.payload);
        state.currentUpload = null;
      })
      .addCase(uploadDocument.rejected, (state, action) => {
        state.uploadStatus = 'failed';
        state.error = action.payload ?? null;
        state.currentUpload = null;
      })

      // Fetch documents reducers
      .addCase(fetchDocuments.pending, (state) => {
        state.fetchStatus = 'loading';
        state.fetchError = null;
      })
      .addCase(fetchDocuments.fulfilled, (state, action: PayloadAction<DocumentDto[]>) => {
        state.fetchStatus = 'succeeded';
        state.documents = action.payload;
      })
      .addCase(fetchDocuments.rejected, (state, action) => {
        state.fetchStatus = 'failed';
        state.fetchError = action.payload ?? null;
        state.documents = [];
      })

      // Delete document reducers
      .addCase(deleteDocument.pending, (state) => {
        state.deleteStatus = 'loading';
        state.deleteError = null;
      })
      .addCase(deleteDocument.fulfilled, (state, action: PayloadAction<string>) => {
        state.deleteStatus = 'succeeded';
        // Remove deleted document from the list
        state.documents = state.documents.filter((doc) => doc.id !== action.payload);
      })
      .addCase(deleteDocument.rejected, (state, action) => {
        state.deleteStatus = 'failed';
        state.deleteError = action.payload ?? null;
      });
  },
});

export const {
  setUploadStatus,
  setUploadProgress,
  setCurrentUpload,
  clearUploadState,
  clearErrors,
} = documentSlice.actions;

export default documentSlice.reducer;
